import { useEffect, useState } from "react";
import { Alert, Button, Field, Input, cx } from "../components/ui";
import { Icon } from "../components/icons";
import { ApiError } from "../lib/api";
import { useAiModels, useOrgs, useSaveInferenceCampaign } from "../lib/queries";
import { money } from "../lib/money";
import type { AdminOrg, AiModel, InferenceCampaign, InferenceCampaignInput } from "../lib/types";

/**
 * Writing a campaign, which is writing a budget.
 *
 * Three of these fields together decide what the platform spends: the quota is
 * the ceiling, the per-key allowance is how fast it drains, and the model list
 * is how much a token costs. So the form shows the arithmetic rather than
 * leaving an operator to do it — a quota of "10,000,000,000" means nothing on
 * its own, and how many tokens it buys at the dearest rate selected is both the
 * figure the campaign is advertised in and the one that has to hold.
 *
 * `active` is deliberately absent on creation, matching promotions: a campaign
 * is written, read back, and then switched on from the list. The alternative is
 * a live budget the instant a half-filled form is submitted.
 */

/** A micro is a millionth of a unit. Picounits do not reach this far. */
const MICROS_PER_UNIT = 1_000_000;

/**
 * What a model sells at, per million output tokens, in whole units.
 *
 * `price.outputMicrosPerMTok` rather than `priceLines`, which is what this read
 * first and is the wrong field for the job: price lines are the per-unit prices
 * of *generation* models — per image, per second — and are documented as empty
 * for every rate-carded model, which is every model a campaign would sensibly
 * use. So the estimate was computing from nothing and rendering its "no rate"
 * fallback, which looks like a legitimate state rather than a broken source.
 *
 * The sell price is also what the platform quotes customers from, so this is
 * now the same basis `campaignTokens` uses on the server. Two different bases
 * would put a different token figure in front of the operator than the one the
 * landing page promises.
 */
function dearestRate(model: AiModel): number {
  return (model.price?.outputMicrosPerMTok ?? 0) / 1_000_000;
}

/**
 * The same four branches, and the same flooring, the landing page and the key
 * email use — this is a preview of their copy, so a budget small enough to read
 * as "0.0M" here has to read the way it will read there.
 */
function compactTokens(n: number): string {
  if (n >= 1_000_000_000_000)
    return `${(Math.floor(n / 100_000_000_000) / 10).toFixed(1)}T`;
  if (n >= 1_000_000_000) return `${(Math.floor(n / 100_000_000) / 10).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(Math.floor(n / 100_000) / 10).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.floor(n / 1_000)}K`;
  return String(n);
}

export function InferenceCampaignForm({
  campaign,
  onDone,
}: {
  campaign?: InferenceCampaign;
  onDone: () => void;
}) {
  const save = useSaveInferenceCampaign(campaign?.id);

  const [code, setCode] = useState(campaign?.code ?? "");
  const [name, setName] = useState(campaign?.name ?? "");
  const [poolOrgId, setPoolOrgId] = useState(campaign?.poolOrgId ?? "");
  const [models, setModels] = useState<string[]>(campaign?.allowedModels ?? []);
  const [keyLimit, setKeyLimit] = useState(
    campaign ? String(campaign.keyMonthlyLimitMicros / MICROS_PER_UNIT) : "5",
  );
  const [quota, setQuota] = useState(
    campaign ? String(campaign.quotaMicros / MICROS_PER_UNIT) : "500",
  );
  const [maxKeys, setMaxKeys] = useState(campaign?.maxKeys ? String(campaign.maxKeys) : "");
  const [endsAt, setEndsAt] = useState(campaign?.endsAt?.slice(0, 10) ?? "");
  const [error, setError] = useState<string | null>(null);

  /*
   * Rates, learned from every result the picker has shown rather than from a
   * second fixed page of the catalogue.
   *
   * The first version priced against `limit: 100`, which was survivable while
   * the picker listed those same hundred rows and stopped being so the moment
   * it gained a search: a model found by searching is usually not in the first
   * hundred, so the estimate quietly lost its input exactly when somebody went
   * looking for something specific. Remembering what has been seen means every
   * model picked here is priced, because picking it required seeing it.
   *
   * Editing an existing campaign is the one case that still starts blank — its
   * models were chosen in an earlier session — and they fill in as soon as a
   * search brings them back. The panel says how many it counted either way.
   */
  const [rates, setRates] = useState<Record<string, number>>({});
  const knownRates = models.filter((id) => rates[id] !== undefined && rates[id]! > 0);
  const dearest = knownRates.reduce((worst, id) => Math.max(worst, rates[id] ?? 0), 0);

  const quotaUnits = Number(quota);
  const keyUnits = Number(keyLimit);

  /*
   * Floor the token count, not the count of millions.
   *
   * `Math.floor(units / dearest) * 1_000_000` -- which is what this was --
   * rounds down to a whole million tokens, so a campaign the server advertises
   * at 14,627,581 read as 14.0M here. Always lower, so it never overstated the
   * promise, but an operator comparing this panel against the live page saw two
   * different numbers for the same campaign and no reason for it.
   */
  const tokenFloor =
    dearest > 0 && quotaUnits > 0 ? Math.floor((quotaUnits / dearest) * 1_000_000) : null;

  /*
   * What a recipient is actually told, which the operator could not see at all
   * before. This figure goes on the landing page, into the email and onto the
   * dashboard card — it is a public promise being authored here, and authoring
   * it blind is how a "$5 allowance" becomes a headline nobody checked.
   *
   * Same arithmetic as `campaignTokens` on the server, from the same sell rate,
   * so the number here is the number they see.
   */
  const tokensPerKey =
    dearest > 0 && keyUnits > 0 ? Math.floor((keyUnits / dearest) * 1_000_000) : null;

  /*
   * Key-months, not keys, and the distinction is the whole of what used to be
   * confusing here. The budget is a total that never resets; the per-key
   * allowance is a *rate* that resets on the 1st. Dividing them gives how many
   * people can spend their full allowance for how long — a hundred for a month
   * and ten for ten months are the same figure and completely different
   * campaigns, so this is never rendered as a headcount.
   */
  const keyMonths = keyUnits > 0 && quotaUnits > 0 ? Math.floor(quotaUnits / keyUnits) : null;
  const cappedKeys = maxKeys.trim() === "" ? null : Number(maxKeys);

  /* With a cap set there is a real answer to "how long does this last", because
     the worst case is bounded: every key spending everything, every month. */
  const monthsAtCap =
    keyMonths !== null && cappedKeys !== null && cappedKeys > 0
      ? keyMonths / cappedKeys
      : null;

  async function submit() {
    setError(null);
    try {
      const payload: InferenceCampaignInput = {
        name: name.trim(),
        allowedModels: models,
        keyMonthlyLimitMicros: Math.round(keyUnits * MICROS_PER_UNIT),
        quotaMicros: Math.round(quotaUnits * MICROS_PER_UNIT),
        maxKeys: maxKeys.trim() === "" ? null : Number(maxKeys),
        endsAt: endsAt === "" ? null : new Date(`${endsAt}T23:59:59Z`).toISOString(),
      };

      await save.mutateAsync(
        campaign ? payload : { ...payload, code: code.trim(), poolOrgId, active: false },
      );
      onDone();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save that campaign.");
    }
  }

  const ready =
    name.trim().length > 0 &&
    models.length > 0 &&
    keyUnits > 0 &&
    quotaUnits > 0 &&
    (campaign ? true : code.trim().length >= 3 && poolOrgId.length > 0);

  return (
    <div className="space-y-6">
      {error && <Alert>{error}</Alert>}

      <Section title="What it is called">
        <div className="grid gap-4 sm:grid-cols-2">
          {!campaign && (
            <Field label="Code" htmlFor="camp-code" hint="Internal. Not shown to recipients.">
              <Input
                id="camp-code"
                value={code}
                placeholder="FREEAI"
                className="font-mono uppercase"
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
          )}
          <Field
            label="Name"
            htmlFor="camp-name"
            hint="Recipients see this, in the email and on the page."
          >
            <Input
              id="camp-name"
              value={name}
              placeholder="Free AI for builders"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
        </div>
      </Section>

      {!campaign && (
        <Section
          title="What pays for it"
          note="This organization's balance is the budget. Topping it up later extends the campaign."
        >
          <OrgPicker value={poolOrgId} onChange={setPoolOrgId} />
        </Section>
      )}

      <Section
        title="What a key may call"
        note="Only these. A recipient cannot change the list, and nothing else on the catalogue is reachable with their key."
      >
        <ModelPicker
          value={models}
          onChange={setModels}
          onRatesSeen={(seen) => setRates((current) => ({ ...current, ...seen }))}
        />
      </Section>

      <Section
        title="What it costs"
        note="Two different things: what one person may spend each month, and what the whole campaign may spend before it stops."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Each key may spend"
            htmlFor="camp-key"
            hint="Per person, per month. Their allowance refills on the 1st."
          >
            <Input
              id="camp-key"
              type="number"
              min="0.01"
              step="0.01"
              value={keyLimit}
              onChange={(e) => setKeyLimit(e.target.value)}
            />
          </Field>
          <Field
            label="The campaign may spend"
            htmlFor="camp-quota"
            hint="In total, ever. This never refills — when it runs out every key stops at once."
          >
            <Input
              id="camp-quota"
              type="number"
              min="1"
              step="1"
              value={quota}
              onChange={(e) => setQuota(e.target.value)}
            />
          </Field>
        </div>

        {/*
          The arithmetic, shown rather than left to the operator. These are what
          the campaign actually commits to, and neither is readable from the
          fields above without doing the sum.
        */}
        {(tokenFloor !== null || keyMonths !== null || tokensPerKey !== null) && (
          <div className="mt-4 space-y-3 rounded-md border border-line bg-surface p-4">
            {/*
              Said as a sentence rather than as two figures in boxes. The
              relationship is the confusing part — one number is a total and the
              other a monthly rate — and two tiles side by side invite exactly
              the reading that they are the same kind of thing.
            */}
            {keyMonths !== null && (
              <p className="text-sm leading-6 text-fg">
                {money(quotaUnits * MICROS_PER_UNIT)} in total, at{" "}
                {money(keyUnits * MICROS_PER_UNIT)} per key each month, covers{" "}
                <strong className="tabular-nums">{keyMonths} key-months</strong> —{" "}
                {keyMonths} people for one month, or {Math.max(1, Math.floor(keyMonths / 12))} for a
                year, if every one of them spends the whole allowance.
              </p>
            )}

            {tokensPerKey !== null && (
              <p className="text-sm leading-6 text-fg-muted">
                Each recipient is told they get{" "}
                <strong className="text-fg tabular-nums">
                  at least {compactTokens(tokensPerKey)} tokens
                </strong>{" "}
                a month. Customers never see the figure in money — this is the promise the landing
                page and the key email make.
              </p>
            )}

            {monthsAtCap !== null && (
              <p className="text-sm leading-6 text-fg-muted">
                With the cap of {cappedKeys} below, the worst case is every key spending everything
                every month — that empties the budget in about{" "}
                <strong className="text-fg tabular-nums">
                  {monthsAtCap >= 1
                    ? `${monthsAtCap.toFixed(monthsAtCap >= 10 ? 0 : 1)} months`
                    : `${Math.max(1, Math.round(monthsAtCap * 30))} days`}
                </strong>
                . Most recipients spend a fraction of theirs, so the real figure is longer.
              </p>
            )}

            <p className="border-t border-line pt-3 text-xs leading-5 text-fg-subtle">
              {tokenFloor !== null ? (
                <>
                  The budget buys at least{" "}
                  <span className="text-fg-muted tabular-nums">
                    {tokenFloor >= 1_000_000_000
                      ? `${(tokenFloor / 1_000_000_000).toFixed(1)}B`
                      : `${(tokenFloor / 1_000_000).toFixed(0)}M`}
                  </span>{" "}
                  tokens, at the dearest rate among the {knownRates.length} of{" "}
                  {models.length} picked {models.length === 1 ? "model" : "models"} whose price is
                  known here. That is the figure to advertise it in.
                </>
              ) : models.length === 0 ? (
                "Pick a model to see what the budget buys in tokens."
              ) : (
                "None of the models picked publishes a rate here, so there is no token figure to show."
              )}
            </p>
          </div>
        )}
      </Section>

      <Section
        title="How many, and until when"
        note="Both optional, and neither is the budget — that stops the campaign on its own whichever of these you set."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="At most this many keys"
            htmlFor="camp-maxkeys"
            hint="How many people are let in. Blank means as many as the budget allows."
          >
            <Input
              id="camp-maxkeys"
              type="number"
              min="1"
              value={maxKeys}
              placeholder="No limit"
              onChange={(e) => setMaxKeys(e.target.value)}
            />
          </Field>
          <Field label="Finishes on" htmlFor="camp-ends" hint="Keys stop working that night.">
            <Input
              id="camp-ends"
              type="date"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </Field>
        </div>
      </Section>

      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-line pt-4">
        <p className="text-xs text-fg-subtle">
          {campaign
            ? "Changes apply to keys already issued, the model list included."
            : "Saved switched off. Nothing is spent until you turn it on."}
        </p>
        <Button disabled={!ready} loading={save.isPending} onClick={() => void submit()}>
          {campaign ? "Save changes" : "Create campaign"}
        </Button>
      </div>
    </div>
  );
}

/** A titled group, so the form reads as four decisions rather than nine fields. */
function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm text-fg">{title}</h3>
        {note && <p className="mt-0.5 max-w-prose text-xs leading-5 text-fg-subtle">{note}</p>}
      </div>
      {children}
    </section>
  );
}

/**
 * The organization whose balance funds the campaign.
 *
 * Searchable rather than a pasted uuid. The first version of this took the id
 * as text on the reasoning that a dropdown of every tenant is a list where the
 * wrong click bills somebody else — which had the risk backwards. A wrong click
 * is visible the moment the name appears; a wrong paste is thirty-six
 * characters nobody proof-reads, and the campaign bills a customer.
 *
 * Suspension is on the row because a suspended organization is the one pick
 * that silently does not work: its keys authorize against a balance the
 * platform has already stopped.
 */
function OrgPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const [q, setQ] = useState("");
  const results = useOrgs({ q: q || undefined, limit: 8 });
  const rows = results.data?.data ?? [];
  const chosen = rows.find((o) => o.id === value);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-4 rounded-md border border-accent/40 bg-accent-soft/40 p-3">
        <div className="min-w-0">
          <p className="truncate text-sm text-fg">{chosen?.name ?? "Selected organization"}</p>
          <p className="ident truncate text-[11px] text-fg-subtle">{chosen?.slug ?? value}</p>
        </div>
        <button
          type="button"
          className="shrink-0 text-xs text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
          onClick={() => {
            onChange("");
            setQ("");
          }}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search organizations by name or slug"
        aria-label="Search for the funding organization"
      />
      <div className="scrollbar-slim max-h-48 overflow-y-auto rounded-md border border-line">
        {results.isPending ? (
          <p className="p-3 text-sm text-fg-subtle">Searching…</p>
        ) : rows.length === 0 ? (
          <p className="p-3 text-sm text-fg-subtle">Nothing matches that.</p>
        ) : (
          <ul className="grid-hairline">
            {rows.map((org: AdminOrg) => (
              <li key={org.id}>
                <button
                  type="button"
                  onClick={() => onChange(org.id)}
                  className="flex w-full items-center justify-between gap-3 p-3 text-left transition-colors duration-200 ease-smooth hover:bg-surface"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-fg">{org.name}</span>
                    <span className="ident block truncate text-[11px] text-fg-subtle">
                      {org.slug}
                    </span>
                  </span>
                  {org.suspendedAt && (
                    <span className="shrink-0 text-[11px] text-warn">suspended</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * The models a campaign key may call.
 *
 * Searchable, and filtered to sellable. The catalogue runs to hundreds of rows
 * and the first version listed the first hundred with no search — so a model
 * past that point could not be picked at all, which is a functional limit
 * wearing the clothes of a scrolling list. Unsellable models are excluded
 * because the platform cannot meter them: picking one builds a campaign that
 * fails on its first call.
 *
 * Selections render above the search as chips. Without that, choosing a model
 * and then searching for another makes the first one disappear, and an operator
 * has no way to see what they have picked except by clearing the box.
 */
function ModelPicker({
  value,
  onChange,
  onRatesSeen,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  /** Every rate this picker has rendered, so the estimate above can use it. */
  onRatesSeen: (rates: Record<string, number>) => void;
}) {
  const [q, setQ] = useState("");
  const results = useAiModels({ q: q || undefined, limit: 12, status: "sellable" });
  const rows = results.data?.data ?? [];

  /*
   * Reported after the render that produced it, never during — setting parent
   * state from a child's render is the "cannot update a component while
   * rendering a different component" warning, and the fix is not to guard it
   * harder but to do it where effects belong.
   *
   * Keyed on the ids rather than the array, because React Query hands back a
   * new array identity for the same page and an effect on `rows` would run on
   * every refetch. The parent merges, so a rate learned three searches ago
   * survives a search that does not contain it.
   */
  const seenKey = rows.map((m) => m.id).join(",");
  useEffect(() => {
    if (rows.length === 0) return;
    const seen: Record<string, number> = {};
    for (const model of rows) seen[model.id] = dearestRate(model);
    onRatesSeen(seen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seenKey]);

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((m) => m !== id) : [...value, id]);
  }

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {value.map((id) => (
            <li key={id}>
              <button
                type="button"
                onClick={() => toggle(id)}
                aria-label={`Remove ${id}`}
                className="flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent-soft/40 py-1 pr-1.5 pl-2.5 font-mono text-xs text-fg transition-colors duration-200 ease-smooth hover:border-danger/40"
              >
                {id}
                <Icon name="close" className="size-3 text-fg-subtle" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search the catalogue by id or name"
        aria-label="Search for a model"
      />

      <div className="scrollbar-slim max-h-52 overflow-y-auto rounded-md border border-line">
        {results.isPending ? (
          <p className="p-3 text-sm text-fg-subtle">Searching…</p>
        ) : rows.length === 0 ? (
          <p className="p-3 text-sm text-fg-subtle">
            Nothing sellable matches that. Only models the meter can bill are offered here.
          </p>
        ) : (
          <ul className="grid-hairline">
            {rows.map((model: AiModel) => {
              const on = value.includes(model.id);
              const rate = dearestRate(model);
              return (
                <li key={model.id}>
                  <label
                    className={cx(
                      "flex cursor-pointer items-center gap-3 p-3 transition-colors duration-200 ease-smooth",
                      on ? "bg-accent-soft/40" : "hover:bg-surface",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(model.id)}
                      className="size-4 accent-accent"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-mono text-xs text-fg">{model.id}</span>
                      <span className="block truncate text-[11px] text-fg-subtle">
                        {model.displayName}
                      </span>
                    </span>
                    <span className="shrink-0 text-[11px] text-fg-subtle tabular-nums">
                      {rate > 0 ? `${money(rate * MICROS_PER_UNIT)} max` : "no rate"}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
